import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import { useDefaultFilterMenuOptions, useNotesTableColumns } from "@/entities/notes-table-v3";
import { useAppDispatch, useAppSelector } from "@/services/store";
import { formatDayJs } from "@/shared/utils";
import {
  DEFAULT_NOTE_TYPES,
  useDeleteNotesByIdsMutation,
  useGetPersonByIdQuery,
  useLazyGetNoteByIdQuery,
} from "@/services/api";
import { createNewTempNote, type NotesManager } from "@/features/notes-manager";
import { DATE_FORMAT } from "@/shared/constants";
import { useAlert } from "@/shared/hooks";
import { getErrorMessage } from "@/services/errors";
import type { MaybeType } from "@/shared/types";
import type { Key } from "react";
import type { TableRowSelection } from "antd/es/table/interface";
import type { NotesTableItem } from "../types";
import { commitmentsPageActions, commitmentsPageSelectors } from "../slice";
import { useTabsManager } from "./use-tabs-manager";
import { useFilters } from "./use-filters";

export const useNotesManager = (): NotesManager => {
  const { personId } = useParams();

  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const { t } = useTranslation();

  const { data: currentPerson } = useGetPersonByIdQuery(
    { id: personId ?? "" },
    { skip: !personId || personId === "TEMP_ID" },
  );

  const [deleteNotesByIds] = useDeleteNotesByIdsMutation();
  const [getNoteById] = useLazyGetNoteByIdQuery();

  const { notesTableColumnsGetters } = useNotesTableColumns();
  const {
    getDefaultPersonGroupOptions,
    getDefaultNoteTypeOptions,
    getDefaultNoteStatusOptions,
    getDefaultDirectionOptions,
  } = useDefaultFilterMenuOptions();
  const tabsManager = useTabsManager();
  const filters = useFilters();

  const dispatch = useAppDispatch();

  const { callError } = useAlert();

  const titleFilter = useAppSelector(commitmentsPageSelectors.selectTitleFilter);
  const noteTitleFilter = useAppSelector(commitmentsPageSelectors.selectNoteTitleFilter);
  const notesTableData = useAppSelector(
    commitmentsPageSelectors.selectNotesTableDataByPersonId(personId ?? ""),
  );
  const selectedNotes = useAppSelector(commitmentsPageSelectors.selectSelectedNotes);
  const isLoading = useAppSelector(commitmentsPageSelectors.selectIsLoading);
  const selectedIds = useAppSelector(commitmentsPageSelectors.selectSelectedIds);
  const swipedNote = useAppSelector(commitmentsPageSelectors.selectSwipedNote);
  const sorter = useAppSelector(commitmentsPageSelectors.selectSorter);
  const stringFilter = useAppSelector(commitmentsPageSelectors.selectStringFilter);
  const hasFilters = useAppSelector(commitmentsPageSelectors.hasFilters);
  const fromDateFilter = useAppSelector(commitmentsPageSelectors.selectFromDateFilter);
  const toDateFilter = useAppSelector(commitmentsPageSelectors.selectToDateFilter);
  const fromDueDateFilter = useAppSelector(commitmentsPageSelectors.selectFromDueDateFilter);
  const toDueDateFilter = useAppSelector(commitmentsPageSelectors.selectToDueDateFilter);
  const personsData = useAppSelector(commitmentsPageSelectors.selectPersons);
  const activeNote = useAppSelector(commitmentsPageSelectors.selectActiveNote);

  const columns = useMemo(
    () => [
      notesTableColumnsGetters.getNotesTableImportantColumn({
        title: "",
        dataIndex: ["important"],
      }),
      notesTableColumnsGetters.getNotesTableNoteDateColumn({
        title: t("common.date"),
        dataIndex: ["noteDate"],
        onChangeFilterDateFrom: (date) => filters.setFromDateFilter(formatDayJs(date)),
        onChangeFilterDateTo: (date) => filters.setToDateFilter(formatDayJs(date)),
        colorize: true,
        defaultValueFilterFrom: fromDateFilter ? dayjs(fromDateFilter, DATE_FORMAT) : undefined,
        defaultValueFilterTo: toDateFilter ? dayjs(toDateFilter, DATE_FORMAT) : undefined,
      }),
      notesTableColumnsGetters.getNotesTableDueDateColumn({
        title: t("notesPage.dueDate"),
        dataIndex: ["dueDate"],
        onChangeFilterDateFrom: (date) => filters.setFromDueDateFilter?.(formatDayJs(date)),
        onChangeFilterDateTo: (date) => filters.setToDueDateFilter?.(formatDayJs(date)),
        defaultValueFilterFrom: fromDueDateFilter
          ? dayjs(fromDueDateFilter, DATE_FORMAT)
          : undefined,
        defaultValueFilterTo: toDueDateFilter ? dayjs(toDueDateFilter, DATE_FORMAT) : undefined,
      }),
      notesTableColumnsGetters.getNotesTableDierctionColumn({
        title: "",
        dataIndex: ["direction"],
        menuFilters: getDefaultDirectionOptions(),
        onChangeMenuFilters: (selected) => filters.setDirectionFilters?.(selected),
      }),
      notesTableColumnsGetters.getNotesTableNoteTitleColumn({
        dataIndex: ["title"],
        title: t("common.noteType.commitment_one"),
        onChangeFilter: filters.setNoteTitleFilter,
        filterValue: noteTitleFilter,
        colorize: true,
      }),
      notesTableColumnsGetters.getNotesTableNoteStatusColumn({
        title: t("common.status.status"),
        dataIndex: ["status"],
        menuFilters: getDefaultNoteStatusOptions(),
        onChangeMenuFilters: (selected: string[]) => filters.setStatusFilters?.(selected),
      }),
    ],
    [
      t,
      filters,
      titleFilter,
      noteTitleFilter,
      getDefaultPersonGroupOptions,
      getDefaultNoteTypeOptions,
      notesTableColumnsGetters,
      fromDateFilter,
      toDateFilter,
    ],
  );

  const onSelectChange = useCallback(
    (selectedKeys: Key[]) => {
      dispatch(commitmentsPageActions.setSelectedIds(selectedKeys));
    },
    [dispatch],
  );

  const rowSelection: TableRowSelection<NotesTableItem> = useMemo(
    () => ({
      selectedNotes,
      onChange: onSelectChange,
      columnWidth: 35,
    }),
    [selectedNotes, onSelectChange],
  );

  const handleClickRow = useCallback(
    (note: NotesTableItem) => {
      getNoteById({ noteId: note.id })
        .unwrap()
        .then((res) => {
          const person = personsData.find((p) => p.id === res.personId) ?? null;
          dispatch(commitmentsPageActions.addSelectedNote({ ...res, person }));
          dispatch(commitmentsPageActions.setActiveNote({ noteId: note.id }));
        })
        .catch((e) => {
          callError({ text: getErrorMessage(e.code) });
        });
    },
    [dispatch, getNoteById, personsData],
  );

  const handleAddTempNote = useCallback(() => {
    if (!currentPerson) return;

    const newNote = {
      ...createNewTempNote(),
      title: t("notesPage.newNote"),
      noteType: { name: DEFAULT_NOTE_TYPES.DEFAULT_COMMITMENT },
      person: currentPerson,
      personId: currentPerson?.id ?? "",
    };

    dispatch(commitmentsPageActions.addSelectedNote(newNote));
    dispatch(commitmentsPageActions.setActiveNote({ noteId: newNote.id }));
  }, [dispatch, t, currentPerson]);

  const deleteSelected = useCallback(
    (ids: string[]) => {
      deleteNotesByIds(ids)
        .unwrap()
        .then(() => {
          setDeleteModalIsOpen(false);
          dispatch(commitmentsPageActions.setSelectedNotes([]));
        })
        .catch((e) => {
          callError({ text: getErrorMessage(e.code) });
        });
    },
    [deleteNotesByIds, dispatch],
  );

  const setSelectedNotes = useCallback(
    (notes: NotesTableItem[]) => {
      dispatch(commitmentsPageActions.setSelectedNotes(notes));
    },
    [dispatch],
  );

  const setSwipedNote = useCallback(
    (noteId: MaybeType<string>) => {
      dispatch(commitmentsPageActions.setSwipedNote(noteId));
    },
    [dispatch],
  );

  const handleOpenModal = useCallback(() => {
    setDeleteModalIsOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setDeleteModalIsOpen(false);
  }, []);

  const selectedTableNotes = useMemo(() => {
    return notesTableData?.filter((note) => selectedIds.includes(note.id)) ?? [];
  }, [selectedIds, notesTableData]);

  return {
    state: {
      columns,
      notesTableData,
      isLoading,
      selectedTableNotes,
      deleteModalIsOpen,
      deleteDisabled: !selectedTableNotes.length,
      selectedIds,
      swipedNote,
      selectedNotes,
      sorter,
      stringFilter,
      emptyNotes: notesTableData.length === 0 && !hasFilters,
      activeNote,
    },
    methods: {
      rowSelection,
      handleClickRow,
      handleAddTempNote,
      deleteSelected,
      handleCloseModal,
      handleOpenModal,
      onSelectChange,
      upsertNote: tabsManager.methods.upsertNote,
      deleteNoteFileById: tabsManager.methods.deleteNoteFileById,
      setSelectedNotes,
      setSwipedNote,
      setStringFilter: filters.setStringFilter,
    },
    tabsManager,
    filters,
  };
};
